// Approve confirmation modal. Per brief §6 Approve workflow: shows the
// variation's number, name, value impact, effective date, and what it
// varies — re-stating the substance before the user confirms. The
// effective date is an always-editable, pre-filled date picker: the
// backend hard-rejects approval when the date is missing, so the user
// can set or correct it in-place here. The operator can also adjust (or
// enter) the value impact in-place — blank means non-financial, zero is
// rejected inline, and a variation whose value is apportioned across
// multiple targets locks the field read-only with a hint pointing at
// Edit variation, since splitting an edited total across targets isn't
// something this dialog can resolve on the operator's behalf. On
// confirm, the caller persists the edited date/value via the standard
// PUT (when changed) then calls the dedicated POST .../approve endpoint.
// A 409 from a concurrent decision (someone else approved or rejected
// the same variation between the user's page-load and click) surfaces
// as an inline Alert with fixed copy — "This variation has already been
// approved or rejected" — regardless of which CCFE branch actually
// fired backend-side. Consistent UX copy beats parroting the API
// message verbatim.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import VariesCell from "./VariesCell";
import CurrencyInput from "../shared/CurrencyInput";
import { readTargets, hasAnyAllocation } from "./variationTargets";
import ValueImpactCell from "./ValueImpactCell";
import { pastDateWarning } from "./pastDateWarning";
import { parseValueImpact } from "./variationFormState";
import { gstSuffix } from "../../../../utils/gstSuffix";
import { isConflictError } from "../../../../services/api";
import type { Sow, Variation } from "../../../../types";

const ALREADY_DECIDED_COPY =
  "This variation has already been approved or rejected. Refresh the page to see the latest status.";

export interface ApproveVariationPayload {
  effectiveDate: string;
  /** Parsed edited value — null = non-financial (blank field). Omitted
   *  entirely when the field was locked (multi-target with allocations),
   *  so the caller never PUTs a value it didn't show as editable. */
  valueImpact?: number | null;
}

interface ApproveVariationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  variation: Variation | null;
  sowsById: Map<string, Sow>;
  gstApplies: boolean | undefined;
  onConfirm: (payload: ApproveVariationPayload) => Promise<unknown>;
  isSubmitting?: boolean;
}

export default function ApproveVariationDialog({
  isOpen,
  onClose,
  variation,
  sowsById,
  gstApplies,
  onConfirm,
  isSubmitting,
}: ApproveVariationDialogProps) {
  const [error, setError] = useState<string | null>(null);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [valueRaw, setValueRaw] = useState("");

  // Pattern 0: re-seed the editable date/value (and clear errors) when
  // the dialog opens. This Modal is eager-mounted, so a plain
  // `useState(() => ...)` initialiser would go stale across opens.
  const seedKey = isOpen ? (variation?.id ?? "open") : null;
  const [seededFor, setSeededFor] = useState<string | null>(null);
  if (seedKey !== null && seedKey !== seededFor) {
    setSeededFor(seedKey);
    setEffectiveDate(variation?.effectiveDate ?? "");
    setValueRaw(
      variation?.valueImpact === null || variation?.valueImpact === undefined
        ? ""
        : String(variation.valueImpact),
    );
    setError(null);
  }
  if (!isOpen && seededFor !== null) {
    setSeededFor(null);
  }

  const targets = variation ? readTargets(variation) : [];
  const valueLocked = targets.length > 1 && hasAnyAllocation(targets);
  const parsedValue = parseValueImpact(valueRaw);
  const valueInvalid =
    !valueLocked && valueRaw.trim() !== "" && (parsedValue === null || parsedValue === 0);

  const handleConfirm = async () => {
    setError(null);
    try {
      await onConfirm(
        valueLocked
          ? { effectiveDate }
          : {
              effectiveDate,
              valueImpact: valueRaw.trim() === "" ? null : parsedValue,
            },
      );
      onClose();
    } catch (err) {
      if (isConflictError(err)) {
        setError(ALREADY_DECIDED_COPY);
        return;
      }
      setError(
        err instanceof Error ? err.message : "Failed to approve variation.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        variation
          ? `Approve Variation ${variation.number}?`
          : "Approve variation"
      }
    >
      <div className="space-y-4">
        {variation && (
          <>
            <p className="text-body text-slate-600 dark:text-slate-300">
              Approving this variation will immediately update the contract
              value calculations. This action is logged and cannot be
              reverted — to retract, create a new offsetting variation.
            </p>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md bg-slate-50 dark:bg-slate-800/60 p-3">
              <div className="sm:col-span-2">
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Variation
                </dt>
                <dd className="text-body text-slate-900 dark:text-slate-100">
                  {variation.name}
                </dd>
              </div>
              <div>
                {valueLocked ? (
                  <>
                    <dt className="text-caption text-slate-500 dark:text-slate-400">
                      Value impact
                    </dt>
                    <dd className="mt-0.5">
                      <ValueImpactCell
                        value={variation.valueImpact ?? null}
                        gstApplies={gstApplies}
                        testId="approve-dialog-value"
                      />
                    </dd>
                    <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
                      This variation apportions its value across{" "}
                      {targets.length} targets — use Edit variation to change
                      it.
                    </p>
                  </>
                ) : (
                  <>
                    <label
                      htmlFor="approve-variation-value-impact"
                      className="text-caption text-slate-500 dark:text-slate-400 block"
                    >
                      Value impact
                    </label>
                    <div className="relative mt-0.5">
                      <span
                        aria-hidden="true"
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400"
                      >
                        $
                      </span>
                      <CurrencyInput
                        id="approve-variation-value-impact"
                        value={valueRaw}
                        onChange={setValueRaw}
                        allowNegative
                        allowDecimal
                        ariaLabel="Value impact"
                        ariaInvalid={valueInvalid}
                        disabled={isSubmitting}
                        placeholder="0"
                        testId="approve-variation-value-impact"
                      />
                    </div>
                    <p className="text-caption text-slate-500 dark:text-slate-400 mt-1">
                      {gstSuffix(gstApplies)} · blank = non-financial
                    </p>
                    {valueInvalid && (
                      <p
                        role="alert"
                        className="text-caption text-red-600 dark:text-red-400 mt-1"
                      >
                        Enter a non-zero amount, or leave blank for a
                        non-financial variation.
                      </p>
                    )}
                  </>
                )}
              </div>
              <div>
                <label
                  htmlFor="approve-variation-effective-date"
                  className="text-caption text-slate-500 dark:text-slate-400 block"
                >
                  Effective date <span className="text-red-600">*</span>
                </label>
                <input
                  id="approve-variation-effective-date"
                  type="date"
                  className="input mt-0.5"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  disabled={isSubmitting}
                />
                {pastDateWarning(effectiveDate) && (
                  <p className="text-caption text-amber-700 dark:text-amber-400 mt-1">
                    {pastDateWarning(effectiveDate)}
                  </p>
                )}
              </div>
              <div>
                <dt className="text-caption text-slate-500 dark:text-slate-400">
                  Varies
                </dt>
                <dd className="mt-0.5">
                  <VariesCell
                    targets={readTargets(variation)}
                    sowsById={sowsById}
                  />
                </dd>
              </div>
            </dl>
          </>
        )}
        {error && <Alert variant="error">{error}</Alert>}
        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={
              isSubmitting || !variation || !effectiveDate || valueInvalid
            }
          >
            Approve
          </Button>
        </div>
      </div>
    </Modal>
  );
}
